import AvatarUploadField from "components/ui/AvatarUploadField";
import ToggleSwitch from "components/ui/ToggleSwitch";
import type { ChangeEvent } from "react";
import type { InstructorFormValues } from "../types";

type Props = {
  form: InstructorFormValues;
  isArabic: boolean;
  uploadingImage: boolean;
  onChange: (updater: (previous: InstructorFormValues) => InstructorFormValues) => void;
  onImageChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

const inputClass =
  "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none transition focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white";

export default function InstructorProfileSection({
  form,
  isArabic,
  uploadingImage,
  onChange,
  onImageChange,
}: Props) {
  return (
    <section className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {isArabic ? "الاسم" : "Name"} <span className="text-red-500">*</span>
          </span>
          <input
            value={form.name}
            onChange={(event) => onChange((previous) => ({ ...previous, name: event.target.value }))}
            placeholder={isArabic ? "اكتب اسم المدرس" : "Enter instructor name"}
            className={inputClass}
          />
        </label>

        <label className="space-y-1">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {isArabic ? "رقم الهاتف" : "Phone"} <span className="text-red-500">*</span>
          </span>
          <input
            value={form.phone}
            onChange={(event) => onChange((previous) => ({ ...previous, phone: event.target.value }))}
            placeholder={isArabic ? "اكتب رقم الهاتف" : "Enter phone number"}
            className={inputClass}
          />
        </label>

        <label className="space-y-1">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {isArabic ? "البريد الإلكتروني (اختياري)" : "Email (optional)"}
          </span>
          <input
            type="email"
            value={form.email}
            onChange={(event) => onChange((previous) => ({ ...previous, email: event.target.value }))}
            placeholder={isArabic ? "البريد الإلكتروني" : "Email address"}
            className={inputClass}
          />
        </label>

        {form.id === null ? (
          <div className="flex min-h-[72px] flex-col justify-center rounded-xl border border-brand-500/20 bg-brand-500/5 px-3 py-2.5">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {isArabic ? "سيتم تفعيل الحساب تلقائيًا" : "The account will be activated automatically"}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {isArabic ? "سيتمكن المدرس من الدخول فور إنشاء الحساب." : "The instructor can sign in immediately after creation."}
            </span>
          </div>
        ) : (
          <div className="flex min-h-[72px] items-end justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2.5 dark:border-white/10">
            <span className="pb-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
              {isArabic ? "الحساب نشط" : "Active account"}
            </span>
            <ToggleSwitch
              checked={form.isActive}
              onChange={(value) => onChange((previous) => ({ ...previous, isActive: value }))}
              aria-label={isArabic ? "الحساب نشط" : "Active account"}
            />
          </div>
        )}
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
          {isArabic ? "صورة المدرس" : "Instructor image"}
        </p>
        <AvatarUploadField
          isArabic={isArabic}
          previewUrl={form.imagePreview}
          storageKey={form.image}
          uploading={uploadingImage}
          onFileChange={onImageChange}
          onRemove={() => onChange((previous) => ({ ...previous, image: "", imagePreview: "" }))}
        />
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
          {isArabic ? "نبذة (اختياري)" : "Bio (optional)"}
        </span>
        <textarea
          value={form.bio}
          onChange={(event) => onChange((previous) => ({ ...previous, bio: event.target.value }))}
          placeholder={isArabic ? "اكتب نبذة مختصرة" : "Add a short bio"}
          rows={3}
          className={inputClass}
        />
      </label>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {isArabic ? "كلمة المرور (اختياري)" : "Password (optional)"}
          </span>
          <input
            type="password"
            value={form.password}
            onChange={(event) => onChange((previous) => ({ ...previous, password: event.target.value }))}
            placeholder={isArabic ? "6 أحرف على الأقل" : "At least 6 characters"}
            className={inputClass}
          />
        </label>

        <label className="space-y-1">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {isArabic ? "تأكيد كلمة المرور" : "Confirm password"}
          </span>
          <input
            type="password"
            value={form.passwordConfirmation}
            onChange={(event) => onChange((previous) => ({ ...previous, passwordConfirmation: event.target.value }))}
            className={inputClass}
          />
        </label>
      </div>
    </section>
  );
}
